import type { NoteTemplate, NoteTemplateItem, NoteTemplatePresetKey } from '@repo/shared-types';

/**
 * Four starting sườns a teacher can pick on the subject page's empty state
 * (spec §13.2, superseding the handoff's three-preset `instrument/exam/custom`
 * — Johnny 2026-09-04). Content is transcribed VERBATIM from the spec: `music`
 * and `art` are the spec's own items, `exam` is handoff §3's `exam` preset
 * unchanged, `custom` starts every group empty ("Tự tạo từ đầu").
 *
 * These are client constants, never persisted — a subject's `noteTemplate`
 * only ever stores the resulting `NoteTemplate` object, not a reference back
 * to the preset key's definition here. Item `id`s below are PLACEHOLDERS:
 * two subjects picking the same preset must never end up sharing an item id,
 * so every consumer goes through `presetWithFreshIds`, never this constant
 * directly.
 */
function item(id: string, name: string, hint: string): NoteTemplateItem {
  return { id, name, hint };
}

export const NOTE_TEMPLATE_PRESETS: Record<NoteTemplatePresetKey, NoteTemplate> = Object.freeze({
  music: {
    presetKey: 'music',
    classItems: [
      item('music-c1', 'Nội dung buổi học', 'Hôm nay lớp học gì, đến đâu'),
      item('music-c2', 'Lưu ý chung', 'Điều cả lớp cần nhớ cho buổi sau'),
    ],
    studentItems: [
      item('music-s1', 'Thế tay', 'Tư thế tay, ngón, cổ tay'),
      item('music-s2', 'Tiết tấu', 'Giữ nhịp, đếm, chỗ còn vội'),
      item('music-s3', 'Nốt nhạc', 'Đọc nốt, vị trí trên khoá, chỗ hay nhầm'),
      item('music-s4', 'Thái độ', 'Tập trung, hợp tác, hỏi bài'),
      item('music-s5', 'Chuyên cần', 'Đi học đủ, có tập ở nhà không'),
    ],
    homeworkItems: [
      item('music-h1', 'Gam / Luyện ngón', 'Gam nào, tốc độ, mấy phút mỗi ngày'),
      item('music-h2', 'Bài cũ', 'Ôn lại đoạn nào, tay nào'),
      item('music-h3', 'Bài mới', 'Tập đến ô nhịp nào'),
      item('music-h4', 'Quay clip gửi thầy cô', 'Bài gì, gửi trước ngày nào'),
    ],
  },
  art: {
    presetKey: 'art',
    classItems: [
      item('art-c1', 'Nội dung buổi học', 'Chủ đề, kỹ thuật hôm nay'),
      item('art-c2', 'Lưu ý chung', 'Điều cả lớp cần nhớ cho buổi sau'),
    ],
    studentItems: [
      item('art-s1', 'Bố cục', 'Sắp xếp hình, tỉ lệ, khoảng trống'),
      item('art-s2', 'Màu sắc', 'Phối màu, đậm nhạt, tô có đều không'),
      item('art-s3', 'Nét vẽ', 'Nét dứt khoát chưa, tạo hình'),
      item('art-s4', 'Thái độ', 'Tập trung, kiên nhẫn, sáng tạo'),
      item('art-s5', 'Chuyên cần', 'Đi học đủ, có vẽ thêm ở nhà không'),
    ],
    homeworkItems: [
      item('art-h1', 'Bài vẽ', 'Vẽ gì, khổ giấy, chất liệu'),
      item('art-h2', 'Chuẩn bị dụng cụ', 'Mang gì cho buổi sau'),
      item('art-h3', 'Chụp ảnh gửi thầy cô', 'Bài nào, gửi trước ngày nào'),
    ],
  },
  exam: {
    presetKey: 'exam',
    classItems: [
      item('exam-c1', 'Nội dung đã dạy', 'Bài nào, phần nào, chữa đề số mấy'),
      item('exam-c2', 'Chữa bài / đề', 'Đề số mấy, câu nào, cách giải đã chốt'),
      item('exam-c3', 'Lưu ý cho buổi sau', 'Mang gì, ôn gì, kiểm tra gì'),
    ],
    studentItems: [
      item('exam-s1', 'Điểm / kết quả', 'Điểm bài kiểm tra, số câu đúng'),
      item('exam-s2', 'Lỗi hay gặp', 'Sai ở bước nào, lặp lại từ buổi trước không'),
      item('exam-s3', 'Thái độ học', 'Tập trung, hỏi bài, làm bài đủ không'),
    ],
    homeworkItems: [
      item('exam-h1', 'Bài tập', 'Trang, câu, đề số'),
      item('exam-h2', 'Ôn lại', 'Phần lý thuyết, công thức nào'),
      item('exam-h3', 'Hạn nộp', 'Buổi sau hay ngày cụ thể'),
    ],
  },
  custom: {
    presetKey: 'custom',
    classItems: [],
    studentItems: [],
    homeworkItems: [],
  },
} satisfies Record<NoteTemplatePresetKey, NoteTemplate>);

/** Empty-state row order on the subject page (spec §13.2): Âm nhạc, Mĩ
 *  thuật, Luyện thi, Tự tạo từ đầu — icons `Music2`, `Palette`,
 *  `PencilRuler`, `Plus` in that same order. */
export const NOTE_TEMPLATE_PRESET_ORDER: NoteTemplatePresetKey[] = ['music', 'art', 'exam', 'custom'];

/** 8 hex characters off `crypto.randomUUID()` — no `nanoid` dependency in the
 *  dashboard (the server keeps using `nanoid` in `apps/api`; this is purely a
 *  client-side placeholder id until the item is saved). Collision odds over
 *  32 bits are irrelevant here: ids only need to be unique within one
 *  template being edited. Exported so the editor can mint one id at a time
 *  for a single "Thêm mục" row, not just a whole preset's worth. */
export function freshItemId(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 8);
}

/**
 * A deep copy of the preset with every item id replaced by a fresh one — the
 * editor is prefilled from this, never from `NOTE_TEMPLATE_PRESETS` directly,
 * so two subjects that both start from `music` never end up sharing an item
 * id (id parity would silently merge their notes' `itemId` history).
 */
export function presetWithFreshIds(key: NoteTemplatePresetKey): NoteTemplate {
  const preset = NOTE_TEMPLATE_PRESETS[key];
  const clone = structuredClone(preset) as NoteTemplate;
  for (const group of [clone.classItems, clone.studentItems, clone.homeworkItems]) {
    for (const it of group) it.id = freshItemId();
  }
  return clone;
}

/** Deep compare against the preset's items ignoring ids — "Preset Âm nhạc" vs
 *  "Từ preset Âm nhạc, đã chỉnh" in the meta line and the editor description. */
export function isPristinePreset(t: NoteTemplate): boolean {
  if (!t.presetKey || t.presetKey === 'custom') return false;
  const preset = NOTE_TEMPLATE_PRESETS[t.presetKey];
  const strip = (items: NoteTemplateItem[]) => items.map((i) => [i.name.trim(), i.hint?.trim() || null]);
  return (['classItems', 'studentItems', 'homeworkItems'] as const).every((g) => JSON.stringify(strip(t[g])) === JSON.stringify(strip(preset[g])));
}
